import { jsx as _jsx } from "react/jsx-runtime";
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withApollo } from '@apollo/client/react/hoc';
import * as types from '../../constants/ActionTypes';
import { DEFAULT_TIMEOUT, getVcsIdBySlug, ISSUES_GROUP } from '../../constants/api';
import ApiV2ModelContext from '../../contexts/ApiV2ModelContext';
import { registerWebhook } from '../../model/registerWebhook';
import ErrorPageContent from '../../components/ErrorPageContent';
import { getRepositoryFullnameFromMatch } from '../../utils/getRepository';
import { clearBranchName, replaceAsciiCharWithSlash, replaceSlashWithAsciiChar } from '../../utils/resolveBranch';
import { HANDLED_ERRORS } from '../../constants/types';
import Branch from '../../model/entities/Branch';
import { RepositoryDetail } from './components/RepositoryDetail';
export const defaultUrl = '/:vcs/:owner/:repository';
export function getActiveBranch(repository, activeBranches) {
    if (!Object.keys(activeBranches).length) {
        throw new Error('Active branches are not set.');
    }
    return activeBranches[repository.id];
}
export class RepositoryPage extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            shouldDispatch: true,
            notFound: false,
            loadingBranches: false,
            processingPercentage: {},
            runningAnalysis: false,
            loadTimeout: null,
            spinnerEnabled: true,
            loaded: false,
            isEmpty: false,
        };
        this.loadRepositoryData = async (branchName, refetch = false, webhookLoading = false) => {
            const { match, dispatch, repository, activeBranches } = this.props;
            const { runningAnalysis } = this.state;
            const repositoryFullname = replaceAsciiCharWithSlash(getRepositoryFullnameFromMatch(match));
            const matchBranch = clearBranchName(match.params.branch);
            let shouldFetchDashboard = true;
            let activeBranchName = matchBranch;
            let fetchBranch = !!matchBranch;
            let activeBranch = null;
            if (repository) {
                try {
                    activeBranch = getActiveBranch(repository, activeBranches);
                }
                catch (error) {
                    console.warn(error);
                }
                const branchByName = matchBranch ? repository.getBranchByName(matchBranch) : null;
                const isBranchSet = branchByName && activeBranch && replaceAsciiCharWithSlash(activeBranch?.name) === replaceAsciiCharWithSlash(branchName ?? '');
                const branch = branchName ? repository.getBranchByName(branchName) : activeBranch;
                activeBranchName = branch?.name;
                shouldFetchDashboard = (branchName && !isBranchSet) || runningAnalysis || refetch;
                fetchBranch = !branch?.main;
            }
            if (shouldFetchDashboard) {
                const vcsId = getVcsIdBySlug(match.params.vcs);
                try {
                    this.setState({ loadingBranches: true });
                    const dashboardResponse = await this.context.getDashboard(vcsId, repositoryFullname, activeBranchName && fetchBranch && activeBranchName);
                    if (dashboardResponse?.repository) {
                        if (repository && !dashboardResponse.repository.active) {
                            dashboardResponse.repository.setWebhookLoading(true);
                        }
                        await dispatch({
                            type: !repository ? types.ADD_REPOSITORY : types.UPDATE_REPOSITORY,
                            payload: dashboardResponse,
                        });
                        await this.setActiveBranch(activeBranchName, webhookLoading);
                        this.setState({ loaded: true, isEmpty: false, loadingBranches: false });
                    }
                    else {
                        this.setState(dashboardResponse);
                    }
                }
                catch (error) {
                    const statusCode = error.networkError?.statusCode;
                    if (HANDLED_ERRORS.includes(statusCode)) {
                        this.setState({
                            notFound: true,
                            loaded: true,
                        });
                    }
                    else {
                        throw error;
                    }
                }
            }
            else {
                this.checkRunningAnalysis();
                this.setState({ loaded: true });
            }
        };
        this.setActiveBranch = async (branchName, reload = false) => {
            const { dispatch, repository, activeBranches } = this.props;
            if (!repository) {
                return;
            }
            let branch;
            try {
                branch = branchName ? repository.getBranchByName(branchName) : repository.getMainBranch();
            }
            catch {
                return this.setState({ isEmpty: true });
            }
            let activeBranch = null;
            try {
                activeBranch = getActiveBranch(repository, activeBranches);
            }
            catch (error) {
                console.warn(error);
            }
            if (activeBranch?.name !== branch.name) {
                await dispatch({
                    type: types.UPDATE_ACTIVE_BRANCH,
                    payload: { repositoryId: repository.id, activeBranch: branch },
                });
            }
            if (branch) {
                await this.showBranch(branch, reload);
                await dispatch({
                    type: types.UPDATE_DEFECTS,
                    payload: {
                        type: ISSUES_GROUP.ALL,
                        defectList: branch.getLastCommit().defectList,
                        repositoryId: repository.id,
                        branchId: branch.id,
                        commitHash: branch.getLastCommit().hash,
                    },
                });
            }
            else {
                this.checkRunningAnalysis();
                return;
            }
        };
        this.registerWebHookAction = async () => {
            const { user, dispatch, repository } = this.props;
            // @ts-expect-error TS2345: Argument of type 'Function' is not assignable to parameter of type '(_: Record<string, any>) => Promise<any>'.
            await registerWebhook(user, repository, dispatch, this.context);
            // @TODO remove after resolving unnecessary loop on component
            window.location.reload();
            // await this.loadRepositoryData('', true, true);
        };
        this.showBranch = async (changedBranch, reload = false) => {
            const { dispatch, repository, match, history } = this.props;
            const { runningAnalysis } = this.state;
            if (changedBranch?.name === match.params.branch && !reload) {
                return;
            }
            let branch;
            try {
                branch = changedBranch ?? repository.getMainBranch();
            }
            catch {
                return this.setState({ isEmpty: true });
            }
            const repositoryFullname = getRepositoryFullnameFromMatch(match);
            let url = match.url;
            if (branch) {
                let urlMatch;
                if (match.params.branch) {
                    const repositoryWithBranchLength = `${repositoryFullname}/${match.params.branch}`.length;
                    const repositoryWithBranchIndex = match.url.lastIndexOf(`${repositoryFullname}/${match.params.branch}`);
                    urlMatch = match.url.substring(repositoryWithBranchIndex, repositoryWithBranchIndex + repositoryWithBranchLength);
                }
                else {
                    const repositoryIndex = match.url.lastIndexOf(repositoryFullname);
                    urlMatch = match.url.substring(repositoryIndex, repositoryIndex + repositoryFullname.length);
                }
                url = match.url.replace(urlMatch, branch.main ? repositoryFullname : `${repositoryFullname}/b-${replaceSlashWithAsciiChar(branch.name)}`);
                if (reload || (match.url !== url && !runningAnalysis)) {
                    history.push(url);
                }
            }
            if (reload) {
                await this.loadRepositoryData(branch.name, true);
            }
            else {
                await dispatch({
                    type: types.UPDATE_ACTIVE_BRANCH,
                    payload: {
                        repositoryId: repository.id,
                        activeBranch: branch,
                    },
                });
            }
            this.checkRunningAnalysis();
        };
        this.checkRunningAnalysis = () => {
            const { activeBranches, repository } = this.props;
            const { processingPercentage } = this.state;
            if (!repository || !activeBranches || !activeBranches[repository.id]?.id) {
                return;
            }
            let activeBranch = null;
            try {
                activeBranch = getActiveBranch(repository, activeBranches);
            }
            catch (error) {
                console.warn(error);
            }
            let isAnalysisRunning = false;
            const isCommitTimedOut = activeBranch?.getLastCommit().isTimedOut();
            if (activeBranch) {
                const hasRunningAnalysis = activeBranch.commitList.hasAnyRunningAnalysis();
                const commitsPercentage = activeBranch.commitList.getCommitsPercentage();
                isAnalysisRunning = hasRunningAnalysis;
                this.setState({ processingPercentage: { ...processingPercentage, ...commitsPercentage }, runningAnalysis: isAnalysisRunning });
            }
            if (isAnalysisRunning && !isCommitTimedOut) {
                this.resetRefreshTimer();
                const timeout = setTimeout(() => this.loadRepositoryData(activeBranch?.name ?? ''), DEFAULT_TIMEOUT);
                // @ts-expect-error TS2322: Type Timeout is not assignable to type number
                this.setState({ loadTimeout: timeout, spinnerEnabled: false });
            }
        };
        this.loadCommitList = async (page) => {
            const { repository, dispatch, activeBranches } = this.props;
            let commits = [];
            let commitList = null;
            let activeBranch = null;
            try {
                activeBranch = getActiveBranch(repository, activeBranches);
            }
            catch (error) {
                console.warn(error);
            }
            const activeBranchCommitList = activeBranch?.commitList;
            if (activeBranchCommitList && activeBranchCommitList.currentPage > page) {
                commits = activeBranchCommitList.commits;
            }
            else {
                const lastCommitFromRepository = activeBranch?.getLastCommit();
                try {
                    commitList = await this.context.getCommitList(activeBranch?.id, lastCommitFromRepository?.datetime);
                }
                catch (error) {
                    const statusCode = error.networkError?.statusCode;
                    if (HANDLED_ERRORS.includes(statusCode)) {
                        this.setState({
                            notFound: true,
                            loaded: true,
                        });
                    }
                    else {
                        throw error;
                    }
                }
                commits = commitList ? [...commits, ...commitList.commits] : commits;
            }
            if (commits.length) {
                const commitListData = {
                    commits: {
                        ...commitList ?? activeBranchCommitList?.commits,
                        commits,
                        branch: activeBranch,
                        currentPage: page,
                        moreCommits: activeBranchCommitList?.currentPage && activeBranchCommitList?.currentPage >= page ? true : commitList?.moreCommits,
                    },
                };
                await dispatch({
                    type: types.UPDATE_REPOSITORY,
                    payload: {
                        ...repository,
                        ...commitListData,
                    },
                });
                await dispatch({
                    type: types.UPDATE_ACTIVE_BRANCH,
                    payload: {
                        repositoryId: repository.id,
                        activeBranch: new Branch({ ...activeBranch }, commitListData.commits, {}),
                    },
                });
                this.setState({
                    loaded: true,
                });
            }
        };
    }
    componentDidMount() {
        const { url, params } = this.props.match;
        const branchName = params.branch && url.includes(replaceSlashWithAsciiChar(params.branch)) ? clearBranchName(params.branch) : '';
        this.loadRepositoryData(branchName);
    }
    componentWillUnmount() {
        this.resetRefreshTimer();
    }
    resetRefreshTimer() {
        if (this.state.loadTimeout !== null) {
            clearTimeout(this.state.loadTimeout);
        }
    }
    render() {
        const { user, match, history, repository, activeBranches } = this.props;
        const { loaded, spinnerEnabled, loadingBranches, processingPercentage, isEmpty, notFound } = this.state;
        const { branch } = match.params;
        if (loaded && notFound) {
            return _jsx(ErrorPageContent, { ...this.props, code: 404 });
        }
        if (branch && branch.substring(0, 2) !== 'b-' && branch !== 'commits') {
            history.replace(match.url.replace(branch, `b-${branch}`));
        }
        let activeBranch = null;
        try {
            activeBranch = repository ? getActiveBranch(repository, activeBranches) : null;
        }
        catch (error) {
            console.warn(error);
        }
        return _jsx(RepositoryDetail, { repository: repository, user: user, match: match, isEmpty: isEmpty, branch: activeBranch ?? undefined, activeBranch: activeBranch, processingPercentage: processingPercentage, loadingBranches: spinnerEnabled && loadingBranches, loaded: loaded, loadRepository: this.loadRepositoryData, showBranch: this.showBranch, loadCommitList: this.loadCommitList, registerWebHookAction: this.registerWebHookAction, history: history });
    }
}
RepositoryPage.contextType = ApiV2ModelContext;
const mapStateToProps = (state) => ({
    repositories: state.repositories,
    user: state.user,
    repository: state.repository.repository,
    activeBranches: state.activeBranches,
});
const mapDispatchToProps = (dispatch) => ({
    dispatch: dispatch,
});
export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(withApollo(RepositoryPage)));
